import React, { useState, useEffect } from 'react';
import { ThemeProvider } from './contexts/ThemeContext';
import { EditorProvider } from './contexts/EditorContext';
import { SessionProvider, useSession } from './contexts/SessionContext';
import { GitHubProvider } from './contexts/GitHubContext';
import { AIProvider, useAI } from './contexts/AIContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ResizableContainer } from './components/Layout/ResizableContainer';
import { LoadProject } from './components/LoadProject/LoadProject';
import { Tutorial, STORAGE_KEY as TUTORIAL_KEY } from './components/Tutorial/Tutorial';
import { useEditor } from './contexts/EditorContext';
import { useTheme } from './contexts/ThemeContext';
import 'highlight.js/styles/atom-one-dark.css';

function AppContent() {
  const { files, loadFiles, openTabs, activeTab, openFile } = useEditor();
  const { loadSession } = useSession();
  const { theme, themeName, setThemeName, fontSize, setFontSize } = useTheme();
  const { welcomeProject, resetWelcome } = useAI();
  const { isAuthenticated, saveProject } = useAuth();
  const [showTutorial, setShowTutorial] = useState(false);
  const prevFilesRef = React.useRef(null);
  const activeProjectIdRef = React.useRef(null);

  useEffect(() => {
    // Try to restore previous session on mount
    const session = loadSession();
    if (session?.files) {
      loadFiles(session.files);
    }
  }, [loadSession, loadFiles]);

  // Trigger welcome when files go from null → loaded
  useEffect(() => {
    if (files && !prevFilesRef.current) {
      welcomeProject(files);
    } else if (!files && prevFilesRef.current) {
      resetWelcome();
      activeProjectIdRef.current = null;
    }
    prevFilesRef.current = files;
  }, [files, welcomeProject, resetWelcome]);

  // Auto-save to cloud when logged in (debounced)
  useEffect(() => {
    if (!isAuthenticated || !files) return;
    const timer = setTimeout(() => {
      const topNames = Object.keys(files).slice(0, 3).join(', ');
      const name = topNames || 'Untitled Project';
      const metadata = { openTabs, activeTab, themeName, fontSize };
      saveProject(name, files, activeProjectIdRef.current, metadata).then((id) => {
        if (id) activeProjectIdRef.current = id;
      });
    }, 3000);
    return () => clearTimeout(timer);
  }, [files, openTabs, activeTab, isAuthenticated, saveProject, themeName, fontSize]);

  const handleProjectLoaded = () => {
    // Show tutorial only if it has never been completed
    if (!localStorage.getItem(TUTORIAL_KEY)) {
      setShowTutorial(true);
    }
  };

  if (!files) {
    return <LoadProject onProjectLoaded={handleProjectLoaded} />;
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Geist+Mono:wght@400;500;600&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          overflow: hidden;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          background: ${theme.bg};
          color: ${theme.text};
        }

        button, input, select, textarea {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }

        ::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }

        ::-webkit-scrollbar-track {
          background: transparent;
        }

        ::-webkit-scrollbar-thumb {
          background: ${theme.border};
          border-radius: 3px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: ${theme.textDim};
        }

        @keyframes fadeUp {
          from {
            opacity: 0;
            transform: translateY(6px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            max-height: 0;
          }
          to {
            opacity: 1;
            max-height: 500px;
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 0.4;
          }
          50% {
            opacity: 1;
          }
        }

        input::placeholder,
        textarea::placeholder {
          color: ${theme.textDim};
        }

        /* Override highlight.js colors to match theme */
        .hljs {
          background: transparent !important;
          color: ${theme.text} !important;
        }

        .hljs-keyword,
        .hljs-selector-tag,
        .hljs-title,
        .hljs-section,
        .hljs-doctag,
        .hljs-name,
        .hljs-strong {
          color: ${theme.syntax?.keyword || '#c678dd'} !important;
          font-weight: 600;
        }

        .hljs-string,
        .hljs-title,
        .hljs-section,
        .hljs-attr,
        .hljs-template-tag,
        .hljs-template-variable,
        .hljs-type,
        .hljs-addition {
          color: ${theme.syntax?.string || '#98c379'} !important;
        }

        .hljs-comment,
        .hljs-quote,
        .hljs-deletion,
        .hljs-meta {
          color: ${theme.syntax?.comment || '#5c6370'} !important;
          font-style: italic;
        }

        .hljs-number,
        .hljs-regexp,
        .hljs-literal,
        .hljs-variable,
        .hljs-template-variable,
        .hljs-link {
          color: ${theme.syntax?.number || '#d19a66'} !important;
        }

        .hljs-built_in,
        .hljs-builtin-name,
        .hljs-function {
          color: ${theme.syntax?.fn || '#61afef'} !important;
        }

        .hljs-tag,
        .hljs-selector-id,
        .hljs-selector-class,
        .hljs-selector-attr,
        .hljs-selector-pseudo {
          color: ${theme.syntax?.tag || '#e06c75'} !important;
        }

        .hljs-class .hljs-title,
        .hljs-type {
          color: ${theme.syntax?.type || '#e5c07b'} !important;
        }

        select {
          cursor: pointer;
        }

        button {
          font-family: inherit;
        }
      `}</style>
      <ResizableContainer />
      {showTutorial && <Tutorial onDone={() => setShowTutorial(false)} />}
    </>
  );
}

class ErrorBoundary extends React.Component {
  state = { error: null };
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, color: '#ff6b6b', fontFamily: 'monospace', background: '#1a1a2e', minHeight: '100vh' }}>
          <h2>Something crashed</h2>
          <pre style={{ whiteSpace: 'pre-wrap', marginTop: 16, color: '#ccc' }}>{this.state.error.message}</pre>
          <pre style={{ whiteSpace: 'pre-wrap', marginTop: 8, color: '#888', fontSize: 12 }}>{this.state.error.stack}</pre>
          <button onClick={() => { localStorage.clear(); window.location.reload(); }}
            style={{ marginTop: 20, padding: '10px 20px', background: '#ff6b6b', border: 'none', borderRadius: 8, color: '#000', cursor: 'pointer', fontWeight: 700 }}>
            Clear storage &amp; reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <EditorProvider>
            <GitHubProvider>
              <AIProvider>
                <SessionProvider>
                  <AppContent />
                </SessionProvider>
              </AIProvider>
            </GitHubProvider>
          </EditorProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
